/**
 * Provides a typed version of `Object.keys`. This is useful when
 * working with object literals in noImplicitAny.
 */
export function typedKeys<T>(obj: T) {
  return Object.keys(obj) as (keyof T)[];
}

/**
 * Provides a typed version of `Object.entries`. This is useful when
 * working with object literals in noImplicitAny.
 */
export function typedEntries<T>(obj: T) {
  type EntryRecord = [keyof T, T[keyof T]];
  return Object.entries(obj) as EntryRecord[];
}

export const typedRecord =
  <TValue>() =>
  <T extends Record<PropertyKey, TValue>>(v: T): { [K in keyof T]: TValue } =>
    v;

/** Typed version of `Object.fromEntries`. */
export const fromEntries = <K extends string, V>(entries: [K, V][]) =>
  Object.fromEntries(entries) as { [s in K]: V };

/**
 * Compile-time assertion that `T` is `never`.
 * Use to verify a union is fully covered, e.g. exhaustiveness checks.
 *
 * @example
 * type Missing = Exclude<"a" | "b", "a">; // "b"
 * type _Check = AssertNever<Missing>; // error: Type '"b"' does not satisfy the constraint 'never'
 */
export type AssertNever<T extends never> = T;

/**
 * Compile-time assertion that `T` is `never`.
 *
 * @example
 * assertNever<Exclude<"a" | "b", "a">>(); // error: Type '"b"' does not satisfy the constraint 'never'
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- type-only assertion
export function assertNever<_ extends never>(): void {}
